'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

interface AnimatedCounterProps {
  /** Target value (e.g., "99%", "100%", "<14") */
  value: string;
  /** Duration of count animation in ms. Default: 2000 */
  duration?: number;
  className?: string;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/** Parse "99%", "<14", "100%" into { prefix, number, suffix } */
function parseValue(value: string): { prefix: string; number: number; suffix: string } {
  const match = value.match(/^([^0-9]*)(\d+)(.*)$/);
  if (!match) return { prefix: '', number: 0, suffix: '' };
  return { prefix: match[1], number: parseInt(match[2], 10), suffix: match[3] };
}

export default function AnimatedCounter({ value, duration = 2000, className = '' }: AnimatedCounterProps) {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.3, triggerOnce: true });
  const [displayValue, setDisplayValue] = useState('0');
  const hasAnimated = useRef(false);
  const { prefix, number: targetNumber, suffix } = parseValue(value);

  const animate = useCallback(() => {
    if (hasAnimated.current) return;
    hasAnimated.current = true;

    const startTime = performance.now();

    function step(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      const currentValue = Math.round(easedProgress * targetNumber);
      setDisplayValue(String(currentValue));

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
  }, [duration, targetNumber]);

  useEffect(() => {
    if (isVisible && !hasAnimated.current) {
      animate();
    }
  }, [isVisible, animate]);

  return (
    <span ref={ref as React.RefObject<HTMLSpanElement>} className={className}>
      {prefix}{displayValue}{suffix}
    </span>
  );
}
